import { Dealer } from '@/types'
import { MapPin, Navigation, Phone, Clock } from 'lucide-react'

interface DealerCardProps {
  dealer: Dealer
  isSelected: boolean
  isHovered: boolean
  onClick: () => void
  onHover: (dealer: Dealer | null) => void
}

export function DealerCard({
  dealer,
  isSelected,
  isHovered,
  onClick,
  onHover,
}: DealerCardProps) {
  const openGoogleMaps = (e: React.MouseEvent) => {
    e.stopPropagation()
    const url = `https://www.google.com/maps/dir/?api=1&destination=${dealer.lat},${dealer.lng}`
    window.open(url, '_blank')
  }

  return (
    <button
      type="button"
      data-dealer-id={dealer.id}
      onClick={onClick}
      onMouseEnter={() => onHover(dealer)}
      onMouseLeave={() => onHover(null)}
      className={`group relative w-[280px] sm:w-[320px] shrink-0 snap-center rounded-xl border-2 bg-white p-4 text-left transition-all duration-200 ${
        isSelected
          ? 'border-[#E10F18] shadow-lg shadow-[#E10F18]/12 scale-[1.02]'
          : isHovered
            ? 'border-[#E10F18]/40 shadow-md'
            : 'border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200'
      }`}
    >
      {/* Indicador superior */}
      <div
        className={`absolute top-0 left-4 right-4 h-[3px] rounded-b-full transition-all duration-200 ${
          isSelected ? 'bg-[#E10F18]' : 'bg-transparent'
        }`}
      />

      <div className="flex flex-col gap-2">
        {/* Name */}
        <h3
          className={`text-sm font-bold leading-tight transition-colors ${
            isSelected ? 'text-[#E10F18]' : 'text-[#1F2240]'
          }`}
        >
          {dealer.name}
        </h3>

        {/* Address */}
        <div className="flex items-start gap-2">
          <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-gray-400" />
          <p className="text-xs leading-snug text-gray-500 line-clamp-2">
            {dealer.address}
          </p>
        </div>

        {/* Phone */}
        {dealer.phone && (
          <div className="flex items-center gap-1.5">
            <Phone className="h-3 w-3 text-gray-400" />
            <span className="text-xs font-medium text-gray-600">
              {dealer.phone}
            </span>
          </div>
        )}

        {/* Schedule */}
        {dealer.schedule && (
          <div className="flex items-center gap-1.5">
            <Clock className="h-3 w-3 text-gray-400" />
            <span className="text-[11px] text-gray-400 truncate">
              {dealer.schedule}
            </span>
          </div>
        )}

        {/* Action — solo visible cuando seleccionado */}
        <div
          className={`flex items-center gap-2 pt-1 transition-all duration-200 ${
            isSelected
              ? 'opacity-100 max-h-10'
              : 'opacity-0 max-h-0 overflow-hidden'
          }`}
        >
          <span
            onClick={openGoogleMaps}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#E10F18] px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm transition-colors hover:bg-[#C40D17] cursor-pointer"
          >
            <Navigation className="h-3 w-3" />
            Cómo llegar
          </span>
        </div>
      </div>
    </button>
  )
}
